import { Component, OnInit, TemplateRef, ViewChild, ViewContainerRef } from '@angular/core';
import { UntypedFormGroup, UntypedFormBuilder, Validators, UntypedFormArray } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import { DSM3ModalService, DSM3NavItem } from '@eredes/dsm3';
import { BehaviorSubject, Subject } from 'rxjs';
import { map, tap } from 'rxjs/operators';

import { RequestPermission } from '../../_core/models/request-permission.model';
import { ProfileService } from '../../_core/services/profile.service';
import { UserService } from '../../_core/services/user.service';
import { AuthService } from '../../auth/auth.service';
import { RequestPermissionService } from '../request-permission.service';
import { RequestService } from '../request.service';

import { RequestDetail } from './request-detail.model';
import { RequestDetailService } from './request-detail.service';

import { GoogleAnalyticsEventsService } from 'app/_core/services/google-analytics-events.service';

@Component({
  selector: 'app-request-detail',
  templateUrl: './request-detail.component.html',
  styleUrls: ['./request-detail.component.scss'],
  standalone: false,
})
export class RequestDetailComponent implements OnInit {
  request: RequestDetail;
  sendCommentForm: UntypedFormGroup = null;
  sendInternalCommentForm: UntypedFormGroup;
  user: any;
  isRet: boolean;

  writePermission: RequestPermission;
  createPermission: RequestPermission;
  internalNotesPermission: RequestPermission;
  permissions: string[] = [];
  requestAttachments: any[] = [];
  maxLength = 200;
  readMoreLabel: ' Mais' | ' Menos' = ' Mais';
  maxMessages = 3;
  maxDocuments = 4;
  requestTypeTitle: string;
  expandMessageAccordeon = true;
  expandDocumentsAccordeon = true;
  breadcrumbsItems: DSM3NavItem[] = [];
  breadcrumbsItems$: Subject<DSM3NavItem[]>;

  commentPlaceholder = 'Insira a sua resposta';
  commentMaxlength = 3500;
  fileTypes: string[] = ['odf', 'pdf', 'doc', 'jpge', 'jpg'];
  fileSizeLimit = 5368709120;
  filePlaceholder = 'Carregar ficheiro';
  @ViewChild('templateCustomModal') templateModal: TemplateRef<any>;
  attachList: [][] = [];

  constructor(
    public fb: UntypedFormBuilder,
    public authService: AuthService,
    protected requestService: RequestService,
    protected userService: UserService,
    protected profileService: ProfileService,
    protected requestPermissionService: RequestPermissionService,
    protected route: ActivatedRoute,
    protected router: Router,
    private readonly modalService: DSM3ModalService,
    private readonly viewContainerRef: ViewContainerRef,
    private readonly requestDetailService: RequestDetailService
  ) {}

  ngOnInit() {
    this.user = this.userService.getUser();
    this.isRet = this.userService.isProfileRetailer();

    if (!this.sendCommentForm) {
      this.sendCommentForm = this.fb.group({
        comment: ['', [Validators.maxLength(3500)], []],
        files: this.fb.array([], [], []),
      });
    } else {
      this.sendCommentForm.reset();
      this.sendCommentForm.get('comment').setErrors(null);
      this.sendCommentForm.markAsPristine();
    }

    if (!this.sendInternalCommentForm) {
      this.sendInternalCommentForm = this.fb.group({
        internalnotes: ['', [Validators.required, Validators.maxLength(3500)]],
      });
    } else {
      this.sendInternalCommentForm.reset();
      this.sendInternalCommentForm.get('internalnotes').setErrors(null);
      this.sendInternalCommentForm.markAsPristine();
    }

    this.route.params.subscribe({
      next: (data) => {
        this.requestService.getRequestDetails(data.id, this.isRet, this.userService.isProfileRealocater()).subscribe(
          (res: any) => {
            this.request = new RequestDetail();
            this.request.detail = res.Result.Collection.detail;
            this.request.fields = res.Result.Collection.fields || [];
            this.request.comments = res.Result.Collection.comments || [];
            this.request.internalComments = res.Result.Collection.commentsInternal || [];
            this.request.realocatable = res.Result.Collection.isRealocatable || false;
            this.request.possibleStatus = res.Result.Collection.possibleStatus || [];

            const requestDetailTitle = this.request.detail.requestOperationType.split(' ');
            this.requestTypeTitle =
              requestDetailTitle[0].charAt(0).toUpperCase() +
              requestDetailTitle[0].slice(1) +
              ' ' +
              requestDetailTitle[1] +
              ' ' +
              requestDetailTitle[2].charAt(0).toUpperCase() +
              requestDetailTitle[2].slice(1);
            this.requestService.getRequestAttachments(this.request.detail.requestUniqueCode).subscribe((files) => {
              this.requestAttachments = files.Result.Collection;
            });

            this.route.data.subscribe((routeData) => {
              if (
                ['pt.edp.request.operation.1', 'pt.edp.request.operation.2'].some((r) =>
                  routeData.requestOperationType.split(',').includes(r)
                )
              ) {
                this.requestPermissionService
                  .getRequestPermissions(this.request.detail.requestAreaUniqueCode)
                  .pipe(
                    map((permissions) => permissions.map((permission) => permission.permissionName)),
                    tap((permissions) =>
                      this.setPermissions(
                        permissions.filter((permissionName) => permissionName.match('_create_')),
                        permissions.filter((permissionName) => permissionName.match('_write_'))
                      )
                    )
                  )
                  .subscribe();
              } else {
                this.requestPermissionService.getPoAndPeWritePermissions().subscribe((permissionData) => {
                  this.setPermissions([], permissionData[0].concat(permissionData[1]) || []);
                });
              }
              this.setBreadcrumb();
              if (
                this.request.detail.requestOperationType === 'Pedido de operação' ||
                this.request.detail.requestOperationTypeUniqueCode === 'pt.edp.request.operation.1'
              ) {
                GoogleAnalyticsEventsService.emitEvent(
                  'Pedidos',
                  'Detalhe Pedido de Operação',
                  'Acede ao detalhe de um Pedido de Operação'
                );
              } else if (
                this.request.detail.requestOperationType === 'Pedido de esclarecimento' ||
                this.request.detail.requestOperationTypeUniqueCode === 'pt.edp.request.operation.2'
              ) {
                GoogleAnalyticsEventsService.emitEvent(
                  'Pedidos',
                  'Detalhe Pedido de Esclarecimento',
                  'Acede ao detalhe de um Pedido de Esclarecimento'
                );
              }

              this.requestDetailService.setServiceClassValues(this.request, this.viewContainerRef, this.isRet);
            });
          },
          (err: any) => {
            console.error(err);
          }
        );
      },
      error: (err) => {
        console.error(err);
        this.router.navigate([''], { relativeTo: this.route });
      },
    });

    this.requestDetailService.closeModal.subscribe((item) => {
      item ? this.ngOnInit() : this.navigateToList();
    });
  }

  get files(): UntypedFormArray {
    return this.sendCommentForm.get('files') as UntypedFormArray;
  }

  /**
   * Improves Request Title
   * @returns readable Request Title
   */
  getRequestTitle(): string {
    const init = this.request.detail.requestOperationType === 'Pedido de operação' ? 'PO' : 'PE';

    return 'Pedido ' + init + ' - ' + this.request.detail.requestMainId.replace('PE-', '').replace('PO-', '');
  }

  /**
   * Sets permissions on logged user for current request
   * @param permissionsCreate cancel or reopen request
   * @param permissionsWrite allow form "Mensagens"
   */
  setPermissions(permissionsCreate: string[], permissionsWrite: string[]) {
    this.createPermission = new RequestPermission(permissionsCreate, this.isRet, !this.isRet, false, true);
    this.writePermission = new RequestPermission(permissionsWrite, this.isRet, !this.isRet, true);
    this.internalNotesPermission = new RequestPermission(permissionsWrite, false, true, true);
  }

  /**
   * Sets info for breadcrumbs, and pushes to component
   */
  private setBreadcrumb(): void {
    this.breadcrumbsItems = [];
    this.breadcrumbsItems$ = new Subject<DSM3NavItem[]>();
    this.breadcrumbsItems.push(
      {
        label: this.getRouteBreadcrumb().title,
        href: this.getRouteBreadcrumb().route,
        active: false,
      },
      {
        label: this.getRequestTitle(),
        active: true,
      }
    );

    this.breadcrumbsItems$ = new BehaviorSubject<DSM3NavItem[]>(this.breadcrumbsItems);
  }

  /**
   * Gets info for breadcrumbs
   * @returns Breadcrumb object
   */
  private getRouteBreadcrumb(): { title: string; route: string } {
    return { title: 'Histórico', route: '/request/history' };
  }

  /**
   * Whether log user create Internal Comment
   * @returns boolean, true is user can see Internal Comment Form
   */
  showCommentForm(): boolean {
    return this.request && this.request.detail.statusNewComment === '1';
  }

  /**
   * Whether log user can perform more actions to the request
   * @returns boolean, true is user can see dropdown with "Ações" to perform on request
   */
  showActions(): boolean {
    if (this.request.detail.statusUniqueCode === 'pt.edp.request.status.4') {
      return false;
    } else if (this.request.detail.statusUniqueCode === 'pt.edp.request.status.3') {
      return false;
    } else {
      return true;
    }
  }

  /**
   * Check that the form update is valid
   * @returns
   */
  checkValidForm(): boolean {
    const { files, comment } = this.sendCommentForm.controls;

    return files.value.length > 0 || comment.value;
  }

  /**
   * Increase/decrease the description and changes button label from "Mais" to "Menos".
   */
  showFullDescription(): void {
    this.maxLength = this.maxLength === 200 ? this.request.detail.requestDescription.length : 200;
    this.readMoreLabel = this.readMoreLabel === ' Mais' ? ' Menos' : ' Mais';
  }

  /**
   * Toggles "Mensagens" that shows 3 (or less) or all
   */
  expandMessages(): void {
    this.maxMessages = !this.expandMessageAccordeon ? 3 : this.request.comments.length;
    this.expandMessageAccordeon = !this.expandMessageAccordeon;
  }

  /**
   * Toggles "Histórico Ficheiros" that shows 4 (or less) or all
   */
  expandDocuments(): void {
    this.maxDocuments = !this.expandDocumentsAccordeon ? 4 : this.requestAttachments.length;
    this.expandDocumentsAccordeon = !this.expandDocumentsAccordeon;
  }

  /**
   * Main function that will download a file
   * @param file clcked file to download
   */
  downloadFile(file: any): void {
    this.requestService.downloadAttachment(file.fileId).subscribe({
      next: (data) => {},
      error: (err) => {
        console.error(`error : ${err.error}`);
      },
    });
  }

  /**
   * Decides which form is fetching comments
   * @param internal 0 - To "Mensagens" with files | 1 - Internal notes just message
   */
  sendComment(internal: number): void {
    const form = internal ? this.sendInternalCommentForm : this.sendCommentForm;
    this.requestDetailService.insertComment(form, internal, 'form', this.files);
  }

  /**
   * Creates a modal with downloadable attachments from comment
   * @param commentUniqueCode comment unique code
   */
  seeAttachmentsList(commentUniqueCode: string): void {
    if (commentUniqueCode) {
      this.requestService.getCommentAttachments(commentUniqueCode).subscribe((data) => {
        this.attachList = data.Result.Collection;

        const modal = this.modalService.custom(this.templateModal, this.viewContainerRef);
        modal.onClose().subscribe(() => {
          this.attachList = [];
        });
      });
    }
  }

  /**
   * Function responsible to reload page
   */
  navigateToList(): void {
    this.router.navigateByUrl(this.getRouteBreadcrumb().route);
  }
}
